import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ShieldCheck,
  Info,
  CalendarRange,
  HeartHandshake,
  CheckCircle2,
  MessageSquare,
  X
} from 'lucide-react';

import ProductImageGallery from '../../../components/customer/ProductDetailComponent.tsx/ProductImageGallery';
import CommonButton from '../../../components/CommonButton';
import ProductDetails from '../../../components/customer/ProductDetailComponent.tsx/ProductDetailsComp';
import AccordionItem from '../../../components/customer/ProductDetailComponent.tsx/AccordionComp';
import PetsYouMayLike from '../../../components/customer/ProductDetailComponent.tsx/PetsYouMayAlsoLike';
import CartDrawer, {
  CartItem
} from '../../../components/customer/ProductDetailComponent.tsx/CartDrawer';

import { Petsections } from '../../../utils/Customer/CustomerData';
import { MixBreed } from '../../../utils/Customer/products';

import {
  useAddPetTocartMutation,
  useLazyGetUserCartQuery,
  useLazyRemoveTocartQuery,
  useUpdateCartMutation
} from '../../../redux/actions/Customer/cartCustomer';

import { useLazyGetPetDetailsQuery } from '../../../redux/actions/Customer/serviceCustomer';
import { useAlert } from '../../../components/customer/AlertContext';
import { REFERRAL_BASE_URL } from '../../../config/apiUrlsCustomer';

interface CategoryItem {
  id: number;
  name: string;
  slug: string;
  parentId: number;
  level: number;
}

// ============================================================
// PET API TYPE
// ============================================================

interface PetData {
  id: number;
  petCode?: string;
  microchipId?: string;

  categories?: CategoryItem[];

  petName: string;
  breed?: string;

  price?: number;
  salePrice?: number | null;
  bookPrice?: number | null;

  productImage?: string;
  subImages?: string[];

  description?: string;
  age?: string;
  gender?: string;

  tag?: string;

  petType?: string;
  petStatus?: string | number;

  color?: string;
  size?: string;
  weight?: number;

  isVaccinated?: boolean;
  vaccinationDetails?: string;

  isKCIRegistered?: boolean;
  isFeatured?: boolean;
  isActive?: boolean;

  adoptionWhatsAppNumber?: string;

  videos?: {
    videoUrl: string;
    thumbnailUrl?: string;
  }[];
}

// ============================================================
// HELPER TO PARSE PET TAXONOMY
// ============================================================

const parsePetTaxonomy = (categories?: CategoryItem[]) => {
  if (!categories || categories.length === 0) {
    return {
      mainCategory: '',
      breedName: '',
      ageRange: ''
    };
  }

  const main = categories.find(
    (c) =>
      c.level === 1 ||
      (c.slug?.startsWith('pets-') && c.level === 1)
  );

  const breed = categories.find(
    (c) =>
      c.slug?.startsWith('breed-') ||
      (c.level === 3 && c.parentId === 302)
  );

  const ageGroup = categories.find((c) =>
    c.slug?.startsWith('age-')
  );

  return {
    mainCategory: main?.name || '',
    breedName: breed?.name || '',
    ageRange: ageGroup?.name || ''
  };
};

// ============================================================
// HELPER TO CALCULATE HUMAN-READABLE AGE
// ============================================================

const calculatePetAge = (dateString?: string) => {
  if (!dateString) return 'N/A';

  const birthDate = new Date(dateString);

  if (isNaN(birthDate.getTime())) {
    return dateString;
  }

  const now = new Date();

  const totalMonths =
    (now.getFullYear() - birthDate.getFullYear()) * 12 +
    (now.getMonth() - birthDate.getMonth());

  if (totalMonths < 1) {
    const diffDays = Math.floor(
      (now.getTime() - birthDate.getTime()) /
      (1000 * 60 * 60 * 24)
    );

    const weeks = Math.max(
      1,
      Math.floor(diffDays / 7)
    );

    return `${weeks} ${weeks === 1 ? 'Week' : 'Weeks'
      }`;
  }

  if (totalMonths < 12) {
    return `${totalMonths} ${totalMonths === 1 ? 'Month' : 'Months'
      }`;
  }

  const years = Math.floor(totalMonths / 12);
  const remMonths = totalMonths % 12;

  return remMonths > 0
    ? `${years} yr ${remMonths} mo`
    : `${years} ${years === 1 ? 'Year' : 'Years'
    }`;
};

// ============================================================
// COMPONENT
// ============================================================

const PetDetailScreen: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showConfirm } = useAlert();

  const [isOpen, setIsOpen] = useState(false);
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [cart, setCart] = useState<CartItem[]>([]);

  const [isMobileLayout, setIsMobileLayout] = useState(
    window.innerWidth < 768
  );

  // ============================================================
  // TERMS / AGREEMENT STATES
  // ============================================================

  const [
    isTermsConfirmationOpen,
    setIsTermsConfirmationOpen
  ] = useState(false);

  const [
    isTermsAndConditionsOpen,
    setIsTermsAndConditionsOpen
  ] = useState(false);

  const [
    pendingPurchaseType,
    setPendingPurchaseType
  ] = useState<number | null>(null);

  const [isTermsAgreed, setIsTermsAgreed] =
    useState(false);

  // ============================================================
  // API
  // ============================================================

  const [
    triggerGetPetDetails,
    {
      data: petData,
      isLoading: isPetLoading,
      error
    }
  ] = useLazyGetPetDetailsQuery();

  const [
    addPetToCart,
    {
      isLoading: isCartActionLoading
    }
  ] = useAddPetTocartMutation();

  const [
    triggerRemoveFromCart
  ] = useLazyRemoveTocartQuery();

  const [
    triggerGetCart
  ] = useLazyGetUserCartQuery();

  const [
    updateCart
  ] = useUpdateCartMutation();

  // ============================================================
  // RESPONSIVE
  // ============================================================

  useEffect(() => {
    const handleResize = () =>
      setIsMobileLayout(
        window.innerWidth < 768
      );

    window.addEventListener(
      'resize',
      handleResize
    );

    return () =>
      window.removeEventListener(
        'resize',
        handleResize
      );
  }, []);

  // ============================================================
  // GET PET + CART
  // ============================================================

  useEffect(() => {
    if (id) {
      triggerGetPetDetails(
        Number(id)
      );
    }

    fetchLatestCart();
  }, [
    id,
    triggerGetPetDetails
  ]);

  // ============================================================
  // CART SYNC
  // ============================================================

  const syncCartWithServer = (res: any) => {
    const productItems =
      res?.productCart?.items || [];

    const petItems =
      res?.petCart?.items || [];

    console.log(
      'PRODUCT CART ITEMS:',
      productItems
    );

    console.log(
      'PET CART ITEMS:',
      petItems
    );

    const mappedPets: CartItem[] =
      petItems.map((item: any) => {
        const petCartItemId =
          item.petCartItemId ??
          item.cartItemId ??
          item.id;

        const petId =
          item.petId ?? 0;

        const petAmount =
          item.amount ??
          item.bookPrice ??
          item.paidAmount ??
          item.totalPrice ??
          item.price ??
          0;

        const petImage =
          item.productImage ??
          item.imageUrl ??
          item.image ??
          '';

        return {
          id: String(
            petCartItemId ?? ''
          ),

          productId:
            Number(petId),

          title:
            item.petName ??
            'Pet Reservation',

          price:
            `AED ${Number(
              petAmount
            ).toFixed(2)}`,

          image:
            petImage,

          quantity: 1,

          isPet: true,

          isMandatory: false,

          purchaseType:
            item.purchaseType,

          totalAmount:
            Number(
              item.originalPrice ??
              item.totalAmount ??
              item.price ??
              item.amount ??
              0
            ),

          paidAmount:
            Number(
              item.paidAmount ??
              item.bookPrice ??
              item.amount ??
              0
            ),

          remainingAmount:
            Number(
              item.remainingAmount ??
              0
            )
        };
      });

    const allItems: CartItem[] = [
      ...mappedPets
    ];

    console.log(
      'FINAL MAPPED CART:',
      allItems
    );

    setCart(allItems);
  };

  // ============================================================
  // FETCH LATEST CART
  // ============================================================

  const fetchLatestCart = async () => {
    try {
      const res =
        await triggerGetCart().unwrap();

      console.log(
        'LATEST CART RESPONSE:',
        res
      );

      syncCartWithServer(res);
    } catch (err) {
      console.error(
        'Error fetching cart updates:',
        err
      );
    }
  };

  // ============================================================
  // ACTUAL ADD TO CART
  // ============================================================

  const handleAddToCart = async (
    purchaseType: number
  ) => {
    if (!petData?.id) return;

    try {
      await addPetToCart({
        petId: Number(petData.id),
        purchaseType
      }).unwrap();

      await fetchLatestCart();

      setIsOpen(true);
    } catch (err: any) {
      const errorMessage =
        err?.data?.Message ||
        err?.data?.message ||
        'Unable to add cart';

      showConfirm({
        title: 'Cart',
        message: errorMessage,
        type: 'error',
        confirmText: 'Ok'
      });
    }
  };

  // ============================================================
  // PURCHASE BUTTON CLICK
  // ============================================================

  const handlePurchaseClick = (
    purchaseType: number
  ) => {
    setPendingPurchaseType(
      purchaseType
    );

    setIsTermsAgreed(false);

    setIsTermsConfirmationOpen(true);
  };

  // ============================================================
  // REMOVE CART ITEM
  // ============================================================

  const handleRemoveCartItem = async (
    cartItemId: string
  ) => {
    try {
      await triggerRemoveFromCart(
        Number(cartItemId)
      ).unwrap();

      await fetchLatestCart();
    } catch (err) {
      console.error(
        'Error running deletion transaction cycle:',
        err
      );
    }
  };

  // ============================================================
  // WHATSAPP
  // ============================================================
  const handleWhatsAppContact = () => {
    if (!petData) return;

    const whatsappNumber =
      petData.adoptionWhatsAppNumber?.trim();

    if (!whatsappNumber) {
      showConfirm({
        title: 'WhatsApp',
        message:
          'WhatsApp contact number is not available for this pet.',
        type: 'error',
        confirmText: 'Ok',
      });

      return;
    }

    // Pet page URL
    const petPageUrl =
      `${REFERRAL_BASE_URL}/productpage/PetDetailScreen/${petData.id}`;

    // Pet details
    const petName =
      petData.petName || 'N/A';

    const color =
      petData.color || 'N/A';

    const microchipNumber =
      petData.microchipId || 'N/A';

    const petImage =
      petData.productImage || '';

    // WhatsApp message
    let textMessage = '';

    if (isAdoptionType) {
      textMessage = `Hello Petland Team 

I am interested in *${petName}*.

*Pet Details*
• Name: ${petName}
• Color: ${color}
• Microchip ID: ${microchipNumber}

*Pet Details*
${petPageUrl}

${petImage
          ? `*Pet Image*
${petImage}

`
          : ''
        }Could you please guide me through the process and next steps?

Thank you.`;
    } else {
      textMessage = `Hello Petland Team

I am interested in *${petName}*.

*Pet Details*
• Name: ${petName}
• Color: ${color}
• Microchip ID: ${microchipNumber}

*Pet Details*
${petPageUrl}

${petImage
          ? `*Pet Image*
${petImage}

`
          : ''
        }Could you please guide me through the process and next steps?

Thank you.`;
    }

    // Clean WhatsApp number
    const cleanNumber =
      whatsappNumber.replace(/[^0-9]/g, '');

    const whatsappUrl =
      `https://wa.me/${cleanNumber}?text=${encodeURIComponent(
        textMessage
      )}`;

    window.open(
      whatsappUrl,
      '_blank'
    );
  };
  // ============================================================
  // LOADING
  // ============================================================

  if (isPetLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-white">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-emerald-600 border-t-transparent" />
      </div>
    );
  }

  // ============================================================
  // ERROR
  // ============================================================

  if (error || !petData) {
    return (
      <div className="flex h-[60vh] flex-col items-center justify-center gap-4 bg-white px-4 text-center">
        <p className="text-gray-500 font-medium text-lg">
          Failed to load pet details profile context.
        </p>

        <CommonButton
          text="Go Back"
          onClick={() =>
            window.history.back()
          }
        />
      </div>
    );
  }

  // ============================================================
  // GALLERY
  // ============================================================

  const galleryProduct = {
    productimage:
      petData.productImage,

    subimage: [
      ...(petData.subImages || []).map(
        (url: string) => ({
          image: url,
          src: url
        })
      ),

      ...(petData.videos || []).map(
        (v: {
          videoUrl: string;
          thumbnailUrl?: string;
        }) => ({
          image: v.videoUrl,
          src: v.videoUrl,
          thumbnail:
            v.thumbnailUrl ||
            petData.productImage
        })
      )
    ],

    videos:
      petData.videos || []
  };

  // ============================================================
  // CATEGORY
  // ============================================================

  const {
    mainCategory,
    breedName,
    ageRange
  } = parsePetTaxonomy(
    petData.categories
  );

  const displayCategory =
    mainCategory ||
    (petData as any).category ||
    'N/A';

  const displayBreed =
    breedName ||
    petData.breed ||
    'N/A';

  const displayAge =
    ageRange ||
    calculatePetAge(
      petData.age
    );

  // ============================================================
  // PET STATUS
  // ============================================================

  const getPetStatusCode = (
    status: any
  ): number | null => {
    if (
      status === null ||
      status === undefined ||
      status === ''
    ) {
      return null;
    }

    if (
      typeof status === 'number' &&
      Number.isFinite(status)
    ) {
      return status;
    }

    const numericStatus =
      Number(status);

    if (
      !Number.isNaN(
        numericStatus
      )
    ) {
      return numericStatus;
    }

    const normalized =
      String(status)
        .trim()
        .toLowerCase();

    switch (normalized) {
      case 'available':
      case 'in stock available':
        return 1;

      case 'reserved':
      case 'reserve':
        return 2;

      case 'sold':
      case 'booked':
        return 3;

      case 'adopted':
        return 4;

      default:
        return null;
    }
  };

  const getDisplayPetStatus = (
    status: any
  ): string => {
    const statusCode =
      getPetStatusCode(status);

    switch (statusCode) {
      case 1:
        return 'In Stock Available';

      case 2:
        return 'Reserved';

      case 3:
        return 'Booked';

      case 4:
        return 'Adopted';

      default:
        return String(
          status || 'Unavailable'
        );
    }
  };

  // ============================================================
  // PET DETAILS
  // ============================================================

  const petDetails = [
    {
      label: 'Breed',
      value:
        displayBreed !== 'N/A'
          ? displayBreed
            .charAt(0)
            .toUpperCase() +
          displayBreed.slice(1)
          : 'N/A'
    },

    {
      label: 'Age',
      value: displayAge
    },

    {
      label: 'Color',
      value: petData.color
        ? petData.color
          .charAt(0)
          .toUpperCase() +
        petData.color.slice(1)
        : 'N/A'
    },

    {
      label: 'Size',
      value: petData.size
        ? petData.size
          .charAt(0)
          .toUpperCase() +
        petData.size.slice(1)
        : 'N/A'
    },

    {
      label: 'Weight',
      value: petData.weight
        ? `${petData.weight} kg`
        : 'N/A'
    },

    {
      label: 'Status',
      value:
        getDisplayPetStatus(
          petData.petStatus
        )
    },

    {
      label: 'Microchip ID',
      value:
        petData.microchipId ||
        'N/A'
    },

    {
      label: 'Vaccination',
      value:
        petData.isVaccinated
          ? 'Fully Vaccinated'
          : 'Up to date'
    },

    {
      label: 'De-wormed',
      value: 'Yes (Up to date)'
    }
  ];

  // ============================================================
  // PURCHASE / AVAILABILITY DATA
  // ============================================================

  const normalizedPetType =
    String(
      petData.petType || ''
    )
      .trim()
      .toLowerCase();

  const petStatusCode =
    getPetStatusCode(
      petData.petStatus
    );

  const isAdoptionType =
    normalizedPetType === 'adopt' ||
    normalizedPetType ===
    'adoption';

  const isPetAvailable =
    petStatusCode === 1;

  const displayPetStatus =
    getDisplayPetStatus(
      petData.petStatus
    );

  const hasPrice =
    petData.price !== undefined &&
    petData.price !== null &&
    Number(petData.price) > 0;

  const hasSalePrice =
    petData.salePrice !== undefined &&
    petData.salePrice !== null &&
    Number(petData.salePrice) > 0;

  const currentSellingPrice =
    hasSalePrice
      ? Number(petData.salePrice)
      : Number(petData.price || 0);

  const isBookPriceAvailable =
    petData.bookPrice !== undefined &&
    petData.bookPrice !== null &&
    Number(petData.bookPrice) > 0;

  const hasAdoptionPrice =
    hasPrice || hasSalePrice;

  // ============================================================
  // ACCORDION
  // ============================================================

  const handleAccordionClick = (
    index: number
  ) => {
    setOpenIndex(
      openIndex === index
        ? null
        : index
    );
  };

  // ============================================================
  // RENDER
  // ============================================================

  return (
    <div className="bg-white min-h-screen text-slate-800 antialiased selection:bg-emerald-100 selection:text-emerald-900">

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12 pt-6 lg:pt-10 text-left">

        {/* ======================================================
            MAIN CONTENT
        ====================================================== */}

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 xl:gap-14 items-start">

          {/* ====================================================
              MEDIA GALLERY
          ==================================================== */}

          <div className="w-full lg:col-span-7 lg:sticky lg:top-[90px] self-start z-10">

            <div className="rounded-2xl overflow-hidden p-2 shadow-sm transition-shadow hover:shadow-md">

              <ProductImageGallery
                product={galleryProduct}
                layout={
                  isMobileLayout
                    ? 'bottom'
                    : 'left'
                }
              />

            </div>
          </div>

          {/* ====================================================
              PET INFO
          ==================================================== */}

          <div className="w-full lg:col-span-5 space-y-6 lg:pt-2">

            <div className="space-y-3">

              <div className="flex flex-wrap items-start justify-between gap-4">

                <div className="space-y-1">

                  {(isAdoptionType && petData.petStatus === 'Available') && (
                      <span className="inline-block bg-amber-50 text-amber-800 text-[10px] font-black border border-amber-200 px-2 py-0.5 rounded uppercase tracking-wider">
                        Available
                      </span>
                    )}

                  <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-950 tracking-tight leading-tight capitalize">
                    {petData.petName}
                  </h1>

                  <p className="text-xs font-semibold text-slate-500 capitalize">
                    {displayBreed !==
                      'N/A'
                      ? `${displayBreed} • `
                      : ''}

                    {displayCategory}

                    {displayAge !==
                      'N/A'
                      ? ` • ${displayAge}`
                      : ''}   {petData.tag !==
                        'N/A'
                        ? ` • ${petData.tag}`
                        : ''}
                  </p>



                </div>

                {petData.gender && (
                  <span
                    className={`px-3 py-1 rounded-full text-[11px] font-extrabold uppercase tracking-widest shadow-xs border ${petData.gender.toLowerCase() ===
                        'female'
                        ? 'bg-rose-50 text-rose-700 border-rose-200/50'
                        : 'bg-sky-50 text-sky-700 border-sky-200/50'
                      }`}
                  >
                    {petData.gender.toLowerCase() ===
                      'female'
                      ? '♀ Female'
                      : '♂ Male'}
                  </span>
                )}

              </div>
            </div>

            {/* ==================================================
                ADOPTION / PURCHASE / AVAILABILITY
            ================================================== */}

            {!isPetAvailable ? (

              <div className="space-y-4 animate-in fade-in duration-300">

                <div className="rounded-2xl border-2 border-red-200 bg-red-50 p-6 text-center">

                  <p className="text-[10px] font-black uppercase tracking-[0.2em] text-red-500">
                    Pet Status
                  </p>

                  <h3 className="mt-1 text-2xl sm:text-3xl font-black uppercase tracking-wide text-red-700">
                    {displayPetStatus}
                  </h3>

                  <p className="mt-2 text-xs font-semibold text-red-600/80">
                    This pet is currently not available for booking or purchase.
                  </p>

                </div>

              </div>

            ) : isAdoptionType ? (

              /* ==================================================
                 AVAILABLE FOR ADOPTION
              ================================================== */

              <div className="space-y-5 animate-in fade-in duration-300">


                {/* ==================================================
                    ADOPTION PROCESS
                ================================================== */}

                <div className="rounded-xl border border-amber-200/60 bg-amber-50/40 p-4 space-y-3 text-xs leading-relaxed">

                  <div className="flex items-center gap-2 font-extrabold uppercase tracking-wider text-amber-900">

                    <Info
                      size={16}
                      className="shrink-0 text-amber-600"
                    />

                    <h4>
                      Our Process
                    </h4>

                  </div>

                  <ol className="list-inside list-decimal space-y-1.5 pl-0.5 font-medium text-slate-600">

                    <li>
                      <span className="font-bold text-slate-800">
                        Contact Us
                      </span>
                      <br />
                      <span className="ml-5">
                        WhatsApp our team to express your interest.
                      </span>
                    </li>

                    <li>
                      <span className="font-bold text-slate-800">
                        Meet the Puppy
                      </span>
                      <br />
                      <span className="ml-5">
                        Schedule a private meeting to meet the puppy.
                      </span>
                    </li>

                    <li>
                      <span className="font-bold text-slate-800">
                        Find Your Match
                      </span>
                      <br />
                      <span className="ml-5">
                        If you feel a connection, we’ll guide you through the next steps.
                      </span>
                    </li>

                  </ol>

                </div>

                {/* ==================================================
                    WHATSAPP BUTTON
                ================================================== */}

                <button
                  type="button"
                  onClick={handleWhatsAppContact}
                  className="flex w-full cursor-pointer items-center justify-center gap-2 rounded-xl bg-[#25D366] px-5 py-3.5 text-xs font-black uppercase tracking-wider text-white transition-all hover:bg-[#20ba56] hover:shadow-md"
                >

                  <MessageSquare
                    size={16}
                    className="fill-white"
                  />

                  Contact on WhatsApp
                </button>

              </div>

            ) : (

              /* ==================================================
                 AVAILABLE FOR SALE
              ================================================== */

              <>

                {/* ==================================================
                    PRICE
                ================================================== */}

                <div className="space-y-4 pt-2">

                  <div className="flex items-baseline justify-between gap-4 border-b border-slate-100 pb-3">

                    <span className="text-xs font-bold uppercase tracking-wider text-slate-400">
                      Total Value
                    </span>

                    <div className="flex items-baseline gap-2">

                      {hasSalePrice ? (
                        <>
                          <span className="text-3xl font-black tracking-tight text-slate-950">
                            AED {Number(
                              petData.salePrice
                            )}
                          </span>

                          {hasPrice && (
                            <span className="text-sm font-semibold text-slate-400 line-through">
                              AED {Number(
                                petData.price
                              )}
                            </span>
                          )}
                        </>
                      ) : (
                        <span className="text-3xl font-black tracking-tight text-slate-950">
                          AED {Number(
                            petData.price || 0
                          )}
                        </span>
                      )}

                    </div>

                  </div>

                  {/* =================================================
                      BOOK PRICE
                  ================================================= */}

                  {isBookPriceAvailable ? (

                    <div className="space-y-4">

                      <div className="flex gap-3 rounded-xl border border-emerald-100/60 bg-emerald-50/40 p-3.5 text-xs leading-relaxed text-slate-600">

                        <ShieldCheck
                          className="mt-0.5 shrink-0 text-emerald-600"
                          size={18}
                        />

                        <p>

                          Secure your pet with a partial reservation payment. Pay the remaining balance within 7 days when taking your pet home.

                          <span className="font-bold text-slate-950">
                            {' '}
                            No refunds on reserved pets. No refunds once the pet is booked or paid in full.
                          </span>

                        </p>

                      </div>

                      <div className="grid grid-cols-2 gap-4 pb-2 text-xs">

                        <div className="space-y-1">

                          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                            Payable Now
                          </p>

                          <p className="text-xl font-black text-emerald-600">
                            AED {Number(
                              petData.bookPrice
                            )}
                          </p>

                        </div>

                        <div className="space-y-1 border-l border-slate-100 pl-4">

                          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                            Pay on Delivery
                          </p>

                          <p className="text-xl font-black text-slate-950">
                            AED {Math.max(
                              0,
                              currentSellingPrice -
                              Number(
                                petData.bookPrice
                              )
                            )}
                          </p>

                        </div>

                      </div>

                    </div>

                  ) : (

                    <div className="flex gap-2 rounded-xl border border-blue-100/40 bg-blue-50/30 px-4 py-3 text-xs text-blue-800">

                      <Info
                        size={16}
                        className="mt-0.5 shrink-0 text-blue-600"
                      />

                      <p>
                        Full payment item. Transaction verification applies upon direct handoff delivery.
                      </p>

                    </div>

                  )}

                </div>

                {/* ==================================================
                    PURCHASE BUTTONS
                ================================================== */}

                <div className="flex flex-col gap-3 pt-1">

                  {/* RESERVE */}

                  {isBookPriceAvailable && (

                    <button
                      type="button"
                      disabled={
                        isCartActionLoading
                      }
                      onClick={() =>
                        handlePurchaseClick(1)
                      }
                      className="group flex w-full cursor-pointer items-center justify-between rounded-xl border-2 border-emerald-600 bg-white px-4 py-3.5 font-bold text-emerald-700 transition-all hover:bg-emerald-600 hover:text-white active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-50"
                    >

                      <div className="flex items-center gap-3 text-left">

                        <CalendarRange
                          size={20}
                          className="text-emerald-600 transition-colors group-hover:text-white"
                        />

                        <div>

                          <p className="text-sm font-extrabold tracking-tight">
                            Reserve & Pay Balance on Delivery
                          </p>

                          <p className="text-[10px] font-medium text-emerald-600/80 transition-colors group-hover:text-emerald-100">
                            Secure now, pay remaining on drop-off
                          </p>

                        </div>

                      </div>

                      <span className="rounded-md bg-emerald-50 px-2.5 py-1 text-sm font-black tracking-tight text-emerald-700 transition-colors group-hover:bg-white/20 group-hover:text-white">
                        AED {Number(
                          petData.bookPrice
                        )}
                      </span>

                    </button>

                  )}

                  {/* FULL PAYMENT */}

                  <button
                    type="button"
                    disabled={
                      isCartActionLoading ||
                      !currentSellingPrice
                    }
                    onClick={() =>
                      handlePurchaseClick(2)
                    }
                    className="flex w-full cursor-pointer items-center justify-between rounded-xl bg-emerald-600 px-4 py-3.5 font-bold text-white shadow-sm transition-all hover:opacity-95 hover:shadow active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-50"
                  >

                    <div className="flex items-center gap-3 text-left">

                      <HeartHandshake
                        size={20}
                        className="text-white/90"
                      />

                      <div>

                        <p className="text-sm font-extrabold tracking-tight">
                          Buy & Pay Full Amount Now
                        </p>

                        <p className="text-[10px] font-medium text-white/70">
                          Complete ownership immediately
                        </p>

                      </div>

                    </div>

                    <span className="rounded-md bg-white/10 px-2.5 py-1 text-sm font-black tracking-tight">
                      AED {currentSellingPrice}
                    </span>

                  </button>

                </div>

                {/* ==================================================
                    WHATSAPP FOR NORMAL PETS
                ================================================== */}

                <button
                  type="button"
                  onClick={handleWhatsAppContact}
                  className="flex w-full cursor-pointer items-center justify-center gap-2 rounded-xl bg-[#25D366] px-5 py-3.5 text-xs font-black uppercase tracking-wider text-white transition-all hover:bg-[#20ba56] hover:shadow-md"
                >

                  <MessageSquare
                    size={16}
                    className="fill-white"
                  />

                  Contact on WhatsApp
                </button>

              </>

            )}

            {/* ==================================================
                BENEFITS
            ================================================== */}

            <div className="grid grid-cols-2 gap-3 text-[11px] font-bold text-slate-500 py-3 border-y border-slate-100">

              <div className="flex items-center gap-2">

                <CheckCircle2
                  size={14}
                  className="text-emerald-500 flex-shrink-0"
                />

                100% Pure Breed Cert.

              </div>

              <div className="flex items-center gap-2">

                <CheckCircle2
                  size={14}
                  className="text-emerald-500 flex-shrink-0"
                />

                Local Health Warranty

              </div>

            </div>

            {/* ==================================================
                PET SPECIFICATIONS
            ================================================== */}

            <div className="pt-2">

              <ProductDetails
                details={petDetails}
                title="Pet Specifications"
              />

            </div>

          </div>

        </div>

        {/* ========================================================
            INFORMATION SECTION
        ======================================================== */}

        <div className="py-8 border-t border-slate-100">

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-start">

            <div className="flex flex-col space-y-6">

              <h3 className="text-xl md:text-2xl font-bold text-slate-900 tracking-tight">
                Information
              </h3>

              {/* Accordion */}

              <div className="flex flex-col space-y-2">

                {Petsections.map(
                  (
                    section,
                    index
                  ) => (
                    <AccordionItem
                      key={
                        section.key
                      }
                      section={{
                        ...section,
                        data:
                          section.key ===
                            'description'
                            ? petData.description
                            : section.data
                      }}
                      isOpen={
                        openIndex ===
                        index
                      }
                      onClick={() =>
                        handleAccordionClick(
                          index
                        )
                      }
                    />
                  )
                )}

              </div>

              {/* Categories */}

              {petData.categories &&
                petData.categories.length >
                0 && (
                  <div className="pt-4 border-t border-slate-100 space-y-1.5">

                    <h4 className="text-xs font-extrabold uppercase tracking-wider text-slate-400">
                      Categories
                    </h4>

                    <div className="flex flex-wrap items-center gap-x-2 text-xs font-bold text-slate-700">

                      {petData.categories.map(
                        (
                          cat: CategoryItem,
                          index: number
                        ) => (
                          <React.Fragment
                            key={
                              cat.id
                            }
                          >

                            <span>
                              {
                                cat.name
                              }
                            </span>

                            {index <
                              petData
                                .categories
                                .length -
                              1 && (
                                <span className="text-slate-300 font-normal">
                                  •
                                </span>
                              )}

                          </React.Fragment>
                        )
                      )}

                    </div>

                  </div>
                )}

            </div>

            {/* Banner */}

            <div className="hidden md:block h-auto overflow-hidden rounded-2xl border border-slate-100 shadow-xs group">

              <img
                src="https://img.freepik.com/free-vector/hand-drawn-pet-grooming-facebook-cover_23-2150051250.jpg?semt=ais_hybrid&w=740&q=80"
                alt="Grooming Banner"
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
              />

            </div>

          </div>

        </div>

      </div>

      {/* ============================================================
          TERMS CONFIRMATION MODAL
      ============================================================ */}

      {isTermsConfirmationOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">

          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => {
              if (isCartActionLoading)
                return;

              setIsTermsConfirmationOpen(
                false
              );

              setPendingPurchaseType(
                null
              );

              setIsTermsAgreed(false);
            }}
          />

          <div className="relative z-10 w-full max-w-sm bg-white rounded-xl shadow-2xl overflow-hidden">

            <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">

              <div>

                <h3 className="text-base font-extrabold text-slate-900">
                  Please Confirm
                </h3>

                <p className="text-[11px] text-slate-500 mt-0.5">
                  Please review before continuing.
                </p>

              </div>

              <button
                type="button"
                disabled={
                  isCartActionLoading
                }
                onClick={() => {
                  setIsTermsConfirmationOpen(
                    false
                  );

                  setPendingPurchaseType(
                    null
                  );

                  setIsTermsAgreed(false);
                }}
                className="w-7 h-7 rounded-full flex items-center justify-center text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition disabled:opacity-50"
              >
                <X size={16} />
              </button>

            </div>

            <div className="px-4 py-4">

              <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5">

                <div className="flex gap-2.5">

                  <Info
                    size={17}
                    className="text-amber-600 shrink-0 mt-0.5"
                  />

                  <div className="text-[12px] leading-relaxed text-slate-700">

                    <p className="font-bold text-slate-900 mb-1">
                      Please Note
                    </p>

                    <p>
                      All pets that are reserved,
                      booked, adopted, or paid for
                      in full are non-refundable.
                      No refunds, returns, or
                      exchanges will be accepted
                      once a pet has been reserved,
                      booked, adopted, or paid for.
                    </p>

                  </div>

                </div>

              </div>

              <label
                className={`mt-4 flex items-start gap-2.5 cursor-pointer select-none ${isCartActionLoading
                    ? 'opacity-60 cursor-not-allowed'
                    : ''
                  }`}
              >

                <input
                  type="checkbox"
                  checked={
                    isTermsAgreed
                  }
                  disabled={
                    isCartActionLoading
                  }
                  onChange={(e) =>
                    setIsTermsAgreed(
                      e.target.checked
                    )
                  }
                  className="mt-0.5 h-4 w-4 shrink-0 accent-emerald-600 cursor-pointer"
                />

                <span className="text-[12px] leading-relaxed text-slate-600">

                  I have read and agree to the{' '}

                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();

                      setIsTermsConfirmationOpen(
                        false
                      );

                      setIsTermsAndConditionsOpen(
                        true
                      );
                    }}
                    className="font-bold text-emerald-700 underline underline-offset-2 hover:text-emerald-800"
                  >
                    Pet Booking Terms & Conditions
                  </button>

                  .

                </span>

              </label>

              <div className="grid grid-cols-2 gap-2.5 mt-4">

                <button
                  type="button"
                  disabled={
                    isCartActionLoading ||
                    !isTermsAgreed ||
                    pendingPurchaseType ===
                    null
                  }
                  onClick={async () => {

                    if (
                      pendingPurchaseType ===
                      null ||
                      !isTermsAgreed
                    ) {
                      return;
                    }

                    const purchaseType =
                      pendingPurchaseType;

                    setIsTermsConfirmationOpen(
                      false
                    );

                    setPendingPurchaseType(
                      null
                    );

                    setIsTermsAgreed(
                      false
                    );

                    await handleAddToCart(
                      purchaseType
                    );
                  }}
                  className={`py-2.5 px-3 rounded-lg text-xs font-extrabold transition-all ${isTermsAgreed
                      ? 'bg-emerald-600 text-white hover:bg-emerald-700 shadow-sm'
                      : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                    }`}
                >
                  {isCartActionLoading
                    ? 'Adding...'
                    : 'Agreed'}
                </button>

                <button
                  type="button"
                  disabled={
                    isCartActionLoading
                  }
                  onClick={() => {
                    setIsTermsConfirmationOpen(
                      false
                    );

                    setPendingPurchaseType(
                      null
                    );

                    setIsTermsAgreed(
                      false
                    );
                  }}
                  className="py-2.5 px-3 rounded-lg border border-slate-200 bg-slate-100 text-slate-700 font-bold text-xs hover:bg-slate-200 transition disabled:opacity-50"
                >
                  No
                </button>

              </div>

            </div>

          </div>

        </div>
      )}

      {/* ============================================================
          TERMS & CONDITIONS MODAL
      ============================================================ */}

      {isTermsAndConditionsOpen && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">

          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => {
              setIsTermsAndConditionsOpen(
                false
              );

              setIsTermsConfirmationOpen(
                true
              );
            }}
          />

          <div className="relative z-10 w-full max-w-lg max-h-[80vh] bg-white rounded-xl shadow-2xl overflow-hidden flex flex-col">

            <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between shrink-0">

              <div>

                <h3 className="text-base font-extrabold text-slate-900">
                  Terms & Conditions
                </h3>

                <p className="text-[11px] text-slate-500 mt-0.5">
                  Pet Booking Terms & Conditions
                </p>

              </div>

              <button
                type="button"
                onClick={() => {
                  setIsTermsAndConditionsOpen(
                    false
                  );

                  setIsTermsConfirmationOpen(
                    true
                  );
                }}
                className="w-7 h-7 rounded-full flex items-center justify-center text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition"
              >
                <X size={16} />
              </button>

            </div>

            <div className="flex-1 overflow-y-auto px-4 py-4">

              <div className="space-y-4 text-[12px] text-slate-600 leading-relaxed">

                <div className="rounded-lg bg-amber-50 border border-amber-200 p-3">

                  <h4 className="font-bold text-amber-900 mb-1.5">
                    Non-Refundable Policy
                  </h4>

                  <p className="text-amber-900/80">
                    All pets that are reserved,
                    booked, adopted, or paid for
                    in full are non-refundable.
                    No refunds, returns, or
                    exchanges will be accepted
                    once a pet has been reserved,
                    booked, adopted, or paid for.
                  </p>

                </div>

                <div>

                  <h4 className="font-bold text-slate-900 mb-1">
                    Pet Reservation
                  </h4>

                  <p>
                    By reserving a pet, you
                    acknowledge that the reservation
                    is subject to the applicable
                    booking conditions and the
                    non-refundable policy stated
                    above.
                  </p>

                </div>

                <div>

                  <h4 className="font-bold text-slate-900 mb-1">
                    Booking
                  </h4>

                  <p>
                    Once a pet has been booked,
                    the transaction is subject
                    to the non-refundable policy.
                  </p>

                </div>

                <div>

                  <h4 className="font-bold text-slate-900 mb-1">
                    Adoption & Full Payment
                  </h4>

                  <p>
                    Once the pet has been adopted
                    or paid for in full, the
                    transaction is considered
                    final and is subject to the
                    applicable Petland policies.
                  </p>

                </div>

                <div>

                  <h4 className="font-bold text-slate-900 mb-1">
                    Customer Agreement
                  </h4>

                  <p>
                    By selecting{' '}
                    <strong>
                      Agreed
                    </strong>
                    , you confirm that you have
                    read and agree to the Pet
                    Booking Terms & Conditions
                    before proceeding with the
                    selected transaction.
                  </p>

                </div>

              </div>

            </div>

            <div className="px-4 py-3 border-t border-slate-100 flex justify-end shrink-0">

              <button
                type="button"
                onClick={() => {
                  setIsTermsAndConditionsOpen(
                    false
                  );

                  setIsTermsConfirmationOpen(
                    true
                  );
                }}
                className="px-4 py-2 rounded-lg bg-emerald-600 text-white font-bold text-xs hover:bg-emerald-700 transition"
              >
                Close
              </button>

            </div>

          </div>

        </div>
      )}

      {/* ============================================================
          CART DRAWER
      ============================================================ */}

      <CartDrawer
        cart={cart}
        isOpen={isOpen}
        onClose={() =>
          setIsOpen(false)
        }

        onIncrement={(itemId) => {
          const item =
            cart.find(
              (i) =>
                i.id === itemId
            );

          if (
            item &&
            !item.isPet
          ) {
            updateCart({
              cartItemId:
                Number(itemId),
              quantity:
                item.quantity + 1
            }).then(
              fetchLatestCart
            );
          }
        }}

        onDecrement={(itemId) => {
          const item =
            cart.find(
              (i) =>
                i.id === itemId
            );

          if (
            item &&
            !item.isPet
          ) {
            if (
              item.quantity <= 1
            ) {
              handleRemoveCartItem(
                itemId
              );
            } else {
              updateCart({
                cartItemId:
                  Number(itemId),
                quantity:
                  item.quantity - 1
              }).then(
                fetchLatestCart
              );
            }
          }
        }}

        onRemove={
          handleRemoveCartItem
        }

        onAddToCart={() => { }}

        handleCheckoutClick={() =>
          navigate(
            '/cartpage/customercartpage'
          )
        }
      />

    </div>
  );
};

export default PetDetailScreen;